import React from 'react';
import { motion } from 'framer-motion';

const VSKRLogo = ({ size = 300, animated = true, className = '', isDark = false, showText = false }) => {
  return (
    <motion.div
      className={`relative ${className}`}
      style={{ width: size, height: showText ? size + 40 : size }}
      initial={animated ? { opacity: 0, scale: 0.8 } : false}
      animate={animated ? { opacity: 1, scale: 1 } : false}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      whileHover={{ scale: 1.12, transition: { duration: 0.3 } }}
    >
      {/* Super Dynamic Cyan Halo - Always Emanating */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: size * 1.6,
          height: size * 1.6,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(0,255,255,0.4) 0%, rgba(0,255,255,0.2) 30%, rgba(0,200,255,0.1) 50%, transparent 70%)',
          filter: 'blur(12px)',
        }}
        animate={{
          opacity: [0.5, 0.9, 0.5],
          scale: [1, 1.15, 1],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      
      {/* Secondary Cyan Pulse */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: size * 1.4,
          height: size * 1.4,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(0,255,255,0.3) 0%, rgba(0,200,255,0.15) 40%, transparent 60%)',
          filter: 'blur(8px)',
        }}
        animate={{
          opacity: [0.7, 1, 0.7],
          scale: [1.05, 1.2, 1.05],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.3,
        }}
      />

      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* Navy Blue Background Gradient */}
            <linearGradient id="royalBlueBg" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#1e3a5f" />
              <stop offset="30%" stopColor="#152d4a" />
              <stop offset="60%" stopColor="#0d1f35" />
              <stop offset="100%" stopColor="#071525" />
            </linearGradient>
            
            {/* Premium Glossy Shine */}
            <linearGradient id="glossyShine" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="white" stopOpacity="0.45" />
              <stop offset="25%" stopColor="white" stopOpacity="0.2" />
              <stop offset="50%" stopColor="white" stopOpacity="0.08" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            
            {/* Rich Gold Gradient */}
            <linearGradient id="goldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffe066" />
              <stop offset="25%" stopColor="#ffd700" />
              <stop offset="50%" stopColor="#daa520" />
              <stop offset="75%" stopColor="#b8860b" />
              <stop offset="100%" stopColor="#996515" />
            </linearGradient>
            
            {/* Gold Shine for 3D effect */}
            <linearGradient id="goldShine" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fff5cc" />
              <stop offset="50%" stopColor="#ffd700" />
              <stop offset="100%" stopColor="#b8860b" />
            </linearGradient>
            
            {/* Electrifying Cyan/Royal Blue Gradient */}
            <linearGradient id="cyanGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00ffff" />
              <stop offset="25%" stopColor="#00bfff" />
              <stop offset="50%" stopColor="#1e90ff" />
              <stop offset="75%" stopColor="#00bfff" />
              <stop offset="100%" stopColor="#00ffff" />
            </linearGradient>
            
            {/* Enhanced Drop Shadow */}
            <filter id="dropShadow" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="6" stdDeviation="12" floodColor="#000" floodOpacity="0.5" />
            </filter>
            
            {/* Gold Glow for balance */}
            <filter id="goldGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feFlood floodColor="#ffd700" floodOpacity="0.6" />
              <feComposite in2="blur" operator="in" />
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            
            {/* Cyan Glow for 5D effect */}
            <filter id="cyanGlow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feFlood floodColor="#00ffff" floodOpacity="0.8" />
              <feComposite in2="blur" operator="in" />
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            
            {/* Radial gradient for depth */}
            <radialGradient id="depthGradient" cx="50%" cy="30%" r="70%" fx="50%" fy="20%">
              <stop offset="0%" stopColor="#1e3a5f" stopOpacity="1" />
              <stop offset="50%" stopColor="#152d4a" stopOpacity="1" />
              <stop offset="100%" stopColor="#071525" stopOpacity="1" />
            </radialGradient>
          </defs>
          
          {/* Main Circle with Royal Blue */}
          <circle cx="150" cy="150" r="140" fill="url(#depthGradient)" filter="url(#dropShadow)" />
          
          {/* Glossy Overlay - Top Shine */}
          <ellipse cx="150" cy="80" rx="105" ry="55" fill="url(#glossyShine)" />
          
          {/* Balance Scale - Enhanced with Glow */}
          <g transform="translate(150, 95)" filter="url(#goldGlow)">
            {/* Center Pole */}
            <rect x="-4" y="0" width="8" height="50" fill="url(#goldGradient)" rx="2" />
            
            {/* Horizontal Bar */}
            <rect x="-55" y="-4" width="110" height="8" rx="4" fill="url(#goldGradient)" />
            
            {/* Left Chain and Pan */}
            <g>
              <line x1="-48" y1="4" x2="-48" y2="22" stroke="url(#goldGradient)" strokeWidth="3" strokeLinecap="round" />
              <path d="M-65 22 Q-48 34 -31 22 L-36 38 Q-48 44 -60 38 Z" fill="url(#goldGradient)" />
            </g>
            
            {/* Right Chain and Pan */}
            <g>
              <line x1="48" y1="4" x2="48" y2="22" stroke="url(#goldGradient)" strokeWidth="3" strokeLinecap="round" />
              <path d="M31 22 Q48 34 65 22 L60 38 Q48 44 36 38 Z" fill="url(#goldGradient)" />
            </g>
            
            {/* Top Ornament */}
            <circle cx="0" cy="-10" r="8" fill="url(#goldGradient)" />
            <circle cx="0" cy="-10" r="4" fill="url(#goldShine)" opacity="0.6" />
          </g>
          
          {/* VSKR Text - Bold and Prominent */}
          <text 
            x="150" 
            y="192" 
            textAnchor="middle" 
            fill="white" 
            fontFamily="Arial Black, Arial, sans-serif" 
            fontSize="48" 
            fontWeight="900" 
            letterSpacing="3"
            style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
          >
            VSKR
          </text>
          
          {/* Legal Text - Title Case, Centered */}
          <text 
            x="150" 
            y="222" 
            textAnchor="middle" 
            fill="white" 
            fontFamily="Arial, sans-serif" 
            fontSize="22" 
            fontWeight="500" 
            letterSpacing="6"
            opacity="0.95"
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
          >
            Legal
          </text>
        </svg>
        
        {/* Rotating Electrifying Pink Circle - 5D Effect */}
        <motion.div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          <svg width={size} height={size} viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
            {/* Main Cyan Ring with Glow */}
            <circle 
              cx="150" 
              cy="150" 
              r="136" 
              fill="none" 
              stroke="url(#cyanGradient)" 
              strokeWidth="5"
              filter="url(#cyanGlow)"
              strokeLinecap="round"
            />
            
            {/* Animated dash for movement effect */}
            <circle 
              cx="150" 
              cy="150" 
              r="136" 
              fill="none" 
              stroke="#00ffff"
              strokeWidth="3"
              strokeDasharray="30 60"
              opacity="0.9"
            />
            
            {/* Bright spots on the ring for 5D effect */}
            <circle cx="150" cy="14" r="6" fill="#00ffff" opacity="0.9">
              <animate attributeName="opacity" values="0.5;1;0.5" dur="1s" repeatCount="indefinite" />
            </circle>
            <circle cx="286" cy="150" r="5" fill="#00bfff" opacity="0.8">
              <animate attributeName="opacity" values="0.4;0.9;0.4" dur="1.2s" repeatCount="indefinite" />
            </circle>
            <circle cx="150" cy="286" r="6" fill="#00ffff" opacity="0.9">
              <animate attributeName="opacity" values="0.6;1;0.6" dur="0.8s" repeatCount="indefinite" />
            </circle>
            <circle cx="14" cy="150" r="5" fill="#00bfff" opacity="0.8">
              <animate attributeName="opacity" values="0.3;0.8;0.3" dur="1.5s" repeatCount="indefinite" />
            </circle>
          </svg>
        </motion.div>
      </div>
      
      {/* VSKR Legal Text Below Logo */}
      {showText && (
        <div className="text-center mt-2">
          <span 
            style={{ 
              fontFamily: 'Arial Black, Arial, sans-serif',
              fontSize: size * 0.18,
              fontWeight: 900,
              color: isDark ? '#ffffff' : '#1e3a5f',
              letterSpacing: '1px',
            }}
          >
            VSKR
          </span>
          <span 
            style={{ 
              fontFamily: 'Arial, sans-serif',
              fontSize: size * 0.12,
              fontWeight: 400,
              color: isDark ? '#d4af37' : '#b8860b',
              marginLeft: '4px',
              letterSpacing: '1px',
            }}
          >
            Legal
          </span>
        </div>
      )}
    </motion.div>
  );
};

export default VSKRLogo;
